// SoloActionRamp.tsx - the Solo dock: the two-tier input ramp, but gated on BOTH the locked read AND the
// typed price (DX7). This is the one real difference from Diagnose's <ActionRamp>, which gates on the
// read alone: Solo will not let you act until you have locked the read AND filled in both price numbers,
// so you can never dodge the math. Picking Raise opens the same manual <BetMathBuilder> (reused verbatim
// from Diagnose). Ported from the mockup #so-acts / #so-lockhint / renderSActs.
import { useState } from 'react';
import type { ActionDef, BetMathDef } from '../diagnose/data';
import { BetMathBuilder } from '../diagnose/BetMathBuilder';

export interface SoloActionRampProps {
  actions: ActionDef[];
  /** the street's manual-raise config (null when there is no raise, e.g. river). */
  bet: BetMathDef | null;
  /** the read must be locked before any action unlocks. */
  locked: boolean;
  /** both price numbers must be filled (true automatically when the street has no price, e.g. preflop). */
  priceReady: boolean;
  /** commit an action. `amount` is present only for a built raise. */
  onCommit: (key: ActionDef['key'], amount?: string) => void;
}

export function SoloActionRamp({ actions, bet, locked, priceReady, onCommit }: SoloActionRampProps) {
  const [building, setBuilding] = useState(false);
  const ready = locked && priceReady;

  // if the gate closes again (revise, or a cleared price) the builder must not linger open.
  if (building && (!ready || !bet)) setBuilding(false);

  if (building && bet && ready) {
    return (
      <BetMathBuilder
        bet={bet}
        onCancel={() => setBuilding(false)}
        onConfirm={(amount) => onCommit('raise', amount)}
      />
    );
  }

  const hint = !locked
    ? 'Lock your read first.'
    : !priceReady
      ? 'Now work out the price, then choose your action.'
      : 'Read and price in. Choose your action.';

  const handle = (a: ActionDef) => {
    if (!ready) return;
    if (a.key === 'raise' && bet) {
      setBuilding(true);
      return;
    }
    onCommit(a.key);
  };

  return (
    <>
      <div className="lockhint">{hint}</div>
      <div className="acts">
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={'btn ' + a.cls}
            disabled={!ready}
            onClick={() => handle(a)}
          >
            {a.label}
            <s>{a.sub}</s>
          </button>
        ))}
      </div>
    </>
  );
}
